import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
    ArrowRight,
    HeartHandshake,
    Compass,
    Smartphone,
    HandHeart,
    Quote,
    Sparkles,
    Building2,
} from "lucide-react";

const VALUE_ITEMS = [
    { icon: Compass, title: "Orientarsi con facilità", text: "Informazioni chiare per comprendere meglio il mondo del condominio." },
    { icon: HandHeart, title: "Comprendere prima di decidere", text: "Ti aiutiamo a conoscere regole, diritti e doveri in modo semplice e accessibile." },
    { icon: HeartHandshake, title: "Favorire una convivenza migliore", text: "Più consapevolezza significa meno incomprensioni e rapporti più sereni." },
    { icon: Smartphone, title: "Il condominio a portata di mano", text: "Professionisti del settore e contenuti utili sempre disponibili quando ne hai bisogno." },
];

function Hero() {
    return (
        <section className="relative overflow-hidden border-b border-slate-200 bg-white">
            <div aria-hidden className="absolute inset-0 bg-[radial-gradient(1200px_500px_at_10%_-10%,rgba(15,76,129,0.10),transparent_65%),radial-gradient(900px_400px_at_100%_10%,rgba(228,161,0,0.08),transparent_60%)]" />
            <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
                <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/20 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-primary" data-testid="about-overline">
                    <Sparkles className="size-3.5" /> Chi siamo
                </div>
                <h1 data-testid="about-title" className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight text-slate-900">
                    Un progetto nato<br />
                    <span className="text-primary">dall'esperienza sul campo.</span>
                </h1>
                <p className="mt-5 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-3xl">
                    CondominioNow è il servizio informativo gratuito di <strong className="text-slate-900">Nowgestioni S.r.l.</strong> Non un'idea nata in un ufficio, ma un progetto pensato dopo anni di ascolto quotidiano di condomini, amministratori e proprietari alle prese con norme, delibere, spese e piccoli grandi problemi di convivenza.
                </p>
                <p className="mt-4 text-lg text-slate-600 leading-relaxed max-w-3xl">
                    Il nostro obiettivo è semplice: <strong className="text-slate-900">rendere le informazioni condominiali accessibili a tutti</strong>, in un linguaggio umano, chiaro e onesto. Perché ogni persona che vive un condominio merita di poter capire prima di decidere.
                </p>
            </div>
        </section>
    );
}

function ValueSection() {
    return (
        <section className="bg-primary text-white relative overflow-hidden" data-testid="about-value">
            <div aria-hidden className="absolute inset-0 bg-[radial-gradient(700px_260px_at_0%_0%,rgba(228,161,0,0.20),transparent_60%),radial-gradient(600px_260px_at_100%_100%,rgba(255,255,255,0.08),transparent_60%)]" />
            <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-16 items-start">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-amber-300">
                            <HeartHandshake className="size-3.5" /> Utilità sociale
                        </div>
                        <h2 data-testid="about-value-title" className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
                            Più informazione,<br />
                            <span className="text-amber-300">meno conflitti.</span>
                        </h2>
                        <p className="mt-5 text-white/80 text-lg leading-relaxed">
                            CondominioNow è un progetto gratuito di utilità sociale. Crediamo che una persona informata sia una persona più serena — e che la comprensione delle regole sia il primo passo verso una convivenza rispettosa.
                        </p>
                        <p className="mt-4 font-serif text-2xl text-amber-300 italic leading-relaxed">
                            Perché una persona informata è una persona più serena.
                        </p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2" data-testid="about-value-cards">
                        {VALUE_ITEMS.map((v) => {
                            const Icon = v.icon;
                            return (
                                <div key={v.title} className="rounded-2xl bg-white/10 border border-white/15 p-5 hover:bg-white/[0.14] transition backdrop-blur-sm">
                                    <Icon className="size-5 text-amber-300" />
                                    <div className="mt-3 font-serif text-lg font-semibold">{v.title}</div>
                                    <p className="mt-1 text-sm text-white/85 leading-relaxed">{v.text}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}

function FounderMessage() {
    return (
        <section className="bg-white" data-testid="about-founder">
            <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                    <Building2 className="size-3.5" /> Il messaggio del fondatore
                </div>
                <h2 data-testid="about-founder-title" className="mt-2 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-slate-900">
                    Perché è nato CondominioNow.
                </h2>

                <div className="mt-10 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-white p-6 sm:p-10 relative overflow-hidden">
                    <Quote className="absolute top-6 right-6 size-16 text-primary/10" aria-hidden />
                    <div className="relative">
                        <p className="font-serif text-xl sm:text-2xl leading-relaxed text-slate-800">
                            "CondominioNow nasce per aiutare i condomini ad orientarsi tra norme, assemblee, spese e decisioni condominiali, offrendo informazioni chiare e facilmente accessibili."
                        </p>
                        <p className="mt-6 font-serif text-lg sm:text-xl leading-relaxed text-slate-700 italic">
                            Perché una persona informata è una persona più serena.
                        </p>

                        <div className="mt-8 flex items-center gap-4 pt-6 border-t border-slate-200">
                            <div className="size-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-serif text-lg font-semibold shrink-0">
                                LC
                            </div>
                            <div>
                                <div className="font-semibold text-slate-900" data-testid="about-signature">Dott. Luigi Colaianni</div>
                                <div className="text-sm text-muted-foreground">Fondatore di CondominioNow</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function FinalCTA() {
    return (
        <section className="bg-slate-50/60 border-t border-slate-200">
            <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-14 text-center">
                <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-slate-900">
                    Pronto a metterci alla prova?
                </h3>
                <p className="mt-3 text-slate-600 max-w-xl mx-auto">
                    Fai una domanda gratuita o sfoglia la biblioteca di risposte e guide pratiche.
                </p>
                <div className="mt-6 flex flex-wrap gap-3 justify-center">
                    <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-full h-12 px-6">
                        <Link to="/registrati">Fai una domanda <ArrowRight className="size-4 ml-1" /></Link>
                    </Button>
                    <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-6 border-slate-300 hover:border-primary hover:text-primary">
                        <Link to="/faq">Sfoglia le FAQ <ArrowRight className="size-4 ml-1" /></Link>
                    </Button>
                </div>
            </div>
        </section>
    );
}

export default function AboutUs() {
    return (
        <div data-testid="about-page">
            <Hero />
            <ValueSection />
            <FounderMessage />
            <FinalCTA />
        </div>
    );
}
